import React, { useState, useEffect } from 'react';
import { FileStack, Download, Loader2, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import DocumentSelector from '../components/documents/DocumentSelector';
import WorkerSelectionPanel from '../components/documents/WorkerSelectionPanel';
import { generateDocument, downloadBlob, ALL_FIELDS, formatDateForInput, formatDateForDocument } from '../utils/documentGenerator';

interface Site {
  id: string;
  name: string;
  address: string;
  start_date: string;
  asbestos_type?: string;
}

interface Worker {
  id: string;
  name: string;
  role: string;
  email: string;
  whitecard_number?: string;
  white_card_number?: string;
  certifications?: string[];
  status: string;
}

interface FormData {
  [key: string]: string;
}

export default function SafetyPaperworkPage() {
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set(['swms', 'arcp', 'demo']));
  const [sites, setSites] = useState<Site[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [formData, setFormData] = useState<FormData>({});
  const [workerSelections, setWorkerSelections] = useState<Record<string, Set<string>>>({
    swms: new Set(),
    arcp: new Set(),
    demo: new Set(),
  });
  const [siteWorkerIds, setSiteWorkerIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [generationResults, setGenerationResults] = useState<Array<{ name: string; success: boolean; error?: string }>>([]);

  useEffect(() => {
    loadSites();
    loadWorkers();

    const today = formatDateForInput(new Date());
    setFormData({
      'date': today,
      'start-date': today,
    });
  }, []);

  useEffect(() => {
    if (selectedSiteId) {
      loadSiteData(selectedSiteId);
      loadSiteWorkers(selectedSiteId);
    }
  }, [selectedSiteId]);

  const loadSites = async () => {
    const { data, error } = await supabase
      .from('sites')
      .select('id, name, address, start_date, asbestos_type')
      .eq('status', 'active')
      .order('name');

    if (error) {
      console.error('Error loading sites:', error);
      return;
    }

    setSites(data || []);
  };

  const loadWorkers = async () => {
    const { data, error } = await supabase
      .from('workers')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error loading workers:', error);
      return;
    }

    setWorkers(data || []);
  };

  const loadSiteData = async (siteId: string) => {
    const { data: site, error } = await supabase
      .from('sites')
      .select(`
        *,
        project_status (
          *,
          hygienist:hygienist_id (
            name,
            phone
          )
        )
      `)
      .eq('id', siteId)
      .single();

    if (error) {
      console.error('Error loading site data:', error);
      return;
    }

    if (site) {
      const newFormData: FormData = { ...formData };
      newFormData['site-address'] = site.address || '';
      newFormData['site-name'] = site.name || '';
      newFormData['start-date'] = site.start_date || formatDateForInput(new Date());

      if (site.project_status?.hygienist_name) {
        const assessorValue = site.project_status.hygienist_phone
          ? `${site.project_status.hygienist_name} - ${site.project_status.hygienist_phone}`
          : site.project_status.hygienist_name;
        newFormData['assessor'] = assessorValue;
      }

      if (site.client_name) {
        newFormData['pc-name'] = site.client_name;
      }
      if (site.client_phone) {
        newFormData['pc-phone'] = site.client_phone;
      }
      if (site.client_address) {
        newFormData['pc-address'] = site.client_address;
      }

      setFormData(newFormData);
    }
  };

  const loadSiteWorkers = async (siteId: string) => {
    const { data, error } = await supabase
      .from('site_assignments')
      .select('worker_id')
      .eq('site_id', siteId)
      .eq('status', 'active');

    if (error) {
      console.error('Error loading site workers:', error);
      return;
    }

    const workerIds = new Set(data?.map(a => a.worker_id) || []);
    setSiteWorkerIds(workerIds);

    setWorkerSelections({
      swms: new Set(workerIds),
      arcp: new Set(workerIds),
      demo: new Set(workerIds),
    });
  };

  const handleToggleDoc = (docId: string) => {
    const newSelected = new Set(selectedDocs);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedDocs(newSelected);
  };

  const handleToggleWorker = (docType: string, workerId: string) => {
    setWorkerSelections(prev => {
      const newSelections = { ...prev };
      const docWorkers = new Set(newSelections[docType]);

      if (docWorkers.has(workerId)) {
        docWorkers.delete(workerId);
      } else {
        docWorkers.add(workerId);
      }

      newSelections[docType] = docWorkers;
      return newSelections;
    });
  };

  const handleFieldChange = (fieldId: string, value: string) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const getWorkerCountsForDocs = () => {
    return {
      swms: workerSelections.swms.size,
      arcp: workerSelections.arcp.size,
      demo: workerSelections.demo.size,
    };
  };

  const prepareDocumentData = (): FormData => {
    const data: FormData = {};
    const todayFormatted = formatDateForDocument(formatDateForInput(new Date()));

    ALL_FIELDS.forEach(field => {
      let value = formData[field.id] || '';

      if (field.id === 'date' || field.id === 'start-date') {
        value = todayFormatted;
      } else if ((field.id === 'date' || field.id === 'start-date') && value) {
        value = formatDateForDocument(value);
      }

      data[field.id] = value;
    });

    return data;
  };

  const handleGenerate = async () => {
    const selectedDocTypes = Array.from(selectedDocs);

    if (selectedDocTypes.length === 0) {
      alert('Please select at least one document to generate');
      return;
    }

    setGenerating(true);
    setProgress(0);
    setGenerationResults([]);

    const documentData = prepareDocumentData();
    const results: Array<{ name: string; success: boolean; error?: string }> = [];

    for (let i = 0; i < selectedDocTypes.length; i++) {
      const docType = selectedDocTypes[i];
      setProgress(Math.round((i / selectedDocTypes.length) * 100));

      try {
        const selectedWorkerIds = Array.from(workerSelections[docType]);
        const today = new Date();
        const formattedDate = today.toLocaleDateString('en-AU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
        const docWorkers = workers
          .filter(w => selectedWorkerIds.includes(w.id))
          .map(w => ({
            id: w.id,
            name: w.name,
            licence: w.whitecard_number || w.white_card_number || '',
            date: formattedDate,
            role: w.role,
            whiteCardNumber: w.whitecard_number || w.white_card_number || '',
          }));

        const { blob, filename } = await generateDocument(
          docType,
          documentData,
          docWorkers,
          selectedSiteId || undefined
        );

        downloadBlob(blob, filename);
        results.push({ name: filename, success: true });
      } catch (error) {
        console.error(`Error generating ${docType}:`, error);
        results.push({
          name: docType,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }

    setProgress(100);
    setGenerationResults(results);
    setGenerating(false);
  };

  const getFieldBadges = (fieldDocs: string[]) => {
    return fieldDocs.map(doc => {
      const colors = {
        swms: 'bg-blue-100 text-blue-800',
        arcp: 'bg-green-100 text-green-800',
        demo: 'bg-red-100 text-red-800',
      };
      return (
        <span
          key={doc}
          className={`px-2 py-0.5 text-xs font-medium rounded ${colors[doc as keyof typeof colors]}`}
        >
          {doc.toUpperCase()}
        </span>
      );
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-orange-100 rounded-lg">
            <FileStack className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Safety Paperwork Generator</h1>
            <p className="text-sm text-gray-600">
              Auto-populate safety documents from site and worker data
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-orange-500 text-white rounded flex items-center justify-center text-sm font-bold">
              1
            </div>
            <h2 className="text-lg font-bold text-gray-900">Select Documents to Generate</h2>
          </div>

          <DocumentSelector
            selectedDocs={selectedDocs}
            onToggleDoc={handleToggleDoc}
            workerCounts={getWorkerCountsForDocs()}
          />

          {Array.from(selectedDocs).filter(docType => docType !== 'arcp').map(docType => (
            <WorkerSelectionPanel
              key={docType}
              workers={workers}
              selectedWorkerIds={workerSelections[docType]}
              onToggleWorker={(workerId) => handleToggleWorker(docType, workerId)}
              documentType={docType}
              siteId={selectedSiteId}
              siteWorkerIds={siteWorkerIds}
            />
          ))}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-orange-500 text-white rounded flex items-center justify-center text-sm font-bold">
              2
            </div>
            <h2 className="text-lg font-bold text-gray-900">Site Selection & Data Entry</h2>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Site to Auto-Fill Data (Optional)
            </label>
            <select
              value={selectedSiteId}
              onChange={(e) => setSelectedSiteId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">-- Manual Entry --</option>
              {sites.map(site => (
                <option key={site.id} value={site.id}>
                  {site.name} - {site.address}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-4">
            <div className="border-l-4 border-orange-500 pl-4">
              <h3 className="text-sm font-bold text-gray-700 mb-3">Job & Site Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ALL_FIELDS.filter(f => ['site-address', 'site-name', 'date', 'start-date'].includes(f.id)).map(field => (
                  <div key={field.id}>
                    <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                      {getFieldBadges(field.docs)}
                      <span className="ml-1">{field.label}</span>
                    </label>
                    <input
                      type={field.id.includes('date') ? 'date' : 'text'}
                      value={formData[field.id] || ''}
                      onChange={(e) => handleFieldChange(field.id, e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="border-l-4 border-orange-500 pl-4">
              <h3 className="text-sm font-bold text-gray-700 mb-3">Business / Contractor Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ALL_FIELDS.filter(f => ['business-contact', 'business-phone', 'removalist-name', 'licence-number', 'prepared-by', 'assessor', 'email', 'contact-phone'].includes(f.id)).map(field => (
                  <div key={field.id}>
                    <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                      {getFieldBadges(field.docs)}
                      <span className="ml-1">{field.label}</span>
                    </label>
                    <input
                      type={field.id === 'email' ? 'email' : 'text'}
                      value={formData[field.id] || ''}
                      onChange={(e) => handleFieldChange(field.id, e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="border-l-4 border-orange-500 pl-4">
              <h3 className="text-sm font-bold text-gray-700 mb-3">Principal Contractor / Client</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ALL_FIELDS.filter(f => ['pc-name', 'pc-phone', 'pc-address'].includes(f.id)).map(field => (
                  <div key={field.id} className={field.id === 'pc-address' ? 'md:col-span-2' : ''}>
                    <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                      {getFieldBadges(field.docs)}
                      <span className="ml-1">{field.label}</span>
                    </label>
                    <input
                      type="text"
                      value={formData[field.id] || ''}
                      onChange={(e) => handleFieldChange(field.id, e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="border-l-4 border-orange-500 pl-4">
              <h3 className="text-sm font-bold text-gray-700 mb-3">Supervisor / Responsible Person</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                    {getFieldBadges(['swms', 'arcp', 'demo'])}
                    <span className="ml-1">Supervisor</span>
                  </label>
                  <input
                    type="text"
                    value={formData['supervisor'] || ''}
                    onChange={(e) => handleFieldChange('supervisor', e.target.value)}
                    list="supervisor-list"
                    placeholder="Select or enter supervisor name"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                  <datalist id="supervisor-list">
                    {workers
                      .filter(w => w.status === 'active')
                      .map(worker => (
                        <option key={worker.id} value={worker.name}>
                          {worker.name} - {worker.role}
                        </option>
                      ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                    {getFieldBadges(['swms', 'arcp', 'demo'])}
                    <span className="ml-1">Responsible Person</span>
                  </label>
                  <input
                    type="text"
                    value={formData['responsible-person'] || ''}
                    onChange={(e) => handleFieldChange('responsible-person', e.target.value)}
                    list="responsible-person-list"
                    placeholder="Select or enter responsible person name"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                  <datalist id="responsible-person-list">
                    {workers
                      .filter(w => w.status === 'active')
                      .map(worker => (
                        <option key={worker.id} value={worker.name}>
                          {worker.name} - {worker.role}
                        </option>
                      ))}
                  </datalist>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg shadow-sm border border-orange-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-orange-500 text-white rounded flex items-center justify-center text-sm font-bold">
              3
            </div>
            <h2 className="text-lg font-bold text-gray-900">Generate Documents</h2>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Each selected document will be downloaded as a pre-filled .docx file with its assigned workers.
          </p>

          <div className="flex flex-wrap gap-2 mb-4">
            {Array.from(selectedDocs).map(docType => {
              const names = { swms: 'Asbestos SWMS', arcp: 'Control Plan', demo: 'Demolition SWMS' };
              const colors = { swms: 'bg-blue-100 text-blue-800 border-blue-300', arcp: 'bg-green-100 text-green-800 border-green-300', demo: 'bg-red-100 text-red-800 border-red-300' };
              return (
                <span key={docType} className={`px-3 py-1.5 text-sm font-semibold rounded-lg border ${colors[docType as keyof typeof colors]}`}>
                  {names[docType as keyof typeof names]}
                </span>
              );
            })}
          </div>

          <button
            onClick={handleGenerate}
            disabled={generating || selectedDocs.size === 0}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generating Documents...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Generate Documents
              </>
            )}
          </button>

          {generating && (
            <div className="mt-4">
              <div className="text-xs font-medium text-gray-700 mb-2">
                Processing... {progress}%
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-orange-500 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {generationResults.length > 0 && (
            <div className="mt-4 space-y-2">
              {generationResults.map((result, idx) => (
                <div
                  key={idx}
                  className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                    result.success
                      ? 'bg-green-100 text-green-800 border border-green-300'
                      : 'bg-red-100 text-red-800 border border-red-300'
                  }`}
                >
                  {result.success ? (
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 flex-shrink-0" />
                  )}
                  <span className="font-medium">{result.name}</span>
                  {result.error && <span className="text-xs">- {result.error}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-1">Template Files Required</p>
              <p>
                Make sure the three template .docx files are uploaded to the Supabase Storage bucket
                named "document-templates". The files should be:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-xs">
                <li>asbestos_licensed_removal_swms_10010-7_1.docx</li>
                <li>asbestos_removal_control_plan.docx</li>
                <li>Demolition_SWMS.docx</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
